import queryString from "query-string";
import React from "react";
import { Alert } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import ClipLoader from "react-spinners/ClipLoader";
import {
  DocuClipperPlanPricing,
  DocuClipperPricing,
  StripeCustomer,
  StripePlan,
  StripeSubscription,
} from "../../../docuclipper/DocuclipperTypes";
import { ReduxState } from "../../../redux";
import { fetchPlans } from "../../../redux/reducers/Plans";
import { fetchSubscription } from "../../../redux/reducers/Subscription";

export const getDiscountedPricing = (
  originalPricing: DocuClipperPricing,
  subscription: StripeSubscription
) => {
  if (!subscription || !subscription.coupon) {
    return null;
  }
  const percent_off = subscription.coupon.percent_off;
  if (!percent_off) {
    return null;
  }
  const discountedPricing: DocuClipperPricing = {} as any;
  for (const plan in originalPricing) {
    for (const billingCycle of [
      "Monthly",
      "Annually",
      "AnnuallyBilledMonthly",
    ]) {
      if (!(plan in discountedPricing)) {
        discountedPricing[plan] = {};
      }
      if (!(billingCycle in discountedPricing[plan])) {
        discountedPricing[plan][billingCycle] = {};
      }
      const obj: DocuClipperPlanPricing = discountedPricing[plan][billingCycle];
      obj.amount = Math.round(
        (originalPricing[plan][billingCycle].amount * (100 - percent_off)) / 100
      );
      obj.annualSavings =
        billingCycle === "Monthly"
          ? null
          : Math.round(
              (originalPricing[plan][billingCycle].annualSavings *
                percent_off) /
                100
            );
      obj.discountSavings = Math.round(
        (originalPricing[plan][billingCycle].discountSavings *
          (100 - percent_off)) /
          100
      );
      obj.discountPercent = originalPricing[plan][billingCycle].discountPercent;
    }
  }
  return discountedPricing;
};

export const getPricing = (plans: StripePlan[]) => {
  plans = plans || [];

  const tierNames = [
    "Starter",
    "Starter100",
    "Professional",
    "Business",
    "Enterprise",
  ] as const;

  const findPlan = (name: string, cycle: string) =>
    plans.find(
      (x) =>
        x.docuclipperPlan.name === name &&
        x.docuclipperPlan.billingCycle === cycle
    );

  const pricing: Partial<DocuClipperPricing> = {};

  for (const tier of tierNames) {
    const monthly = findPlan(tier, "Monthly");
    const annually = findPlan(tier, "Annually");
    const annuallyBilledMonthly = findPlan(tier, "AnnuallyBilledMonthly");

    if (!monthly || !annually) continue;

    const annualSavings =
      (monthly.amount / 100) * 12 - annually.amount / 100;
    const annualSavingsPercentage =
      (100 * annually.amount) / (monthly.amount * 12);

    const tierEntry: any = {
      Monthly: {
        amount: monthly.amount / 100,
        currency: monthly.currency,
        annualSavings: null,
        discountSavings: null,
        discountPercent: null,
      },
      Annually: {
        amount: annually.amount / 100,
        currency: annually.currency,
        annualSavings,
        discountSavings: null,
        discountPercent: annualSavingsPercentage,
      },
    };

    if (annuallyBilledMonthly) {
      const abmSavings =
        ((monthly.amount - annuallyBilledMonthly.amount) * 12) / 100;
      const abmSavingsPercentage =
        (100 * annuallyBilledMonthly.amount) / (monthly.amount * 12);
      tierEntry.AnnuallyBilledMonthly = {
        amount: annuallyBilledMonthly.amount / 100,
        currency: annuallyBilledMonthly.currency,
        annualSavings: abmSavings,
        discountSavings: null,
        discountPercent: abmSavingsPercentage,
      };
    }

    pricing[tier] = tierEntry;
  }

  if (!pricing.Starter) return null;

  return pricing as DocuClipperPricing;
};

export function WithAccountAndPlans(WrappedComponent) {
  return function _WithAccountAndPlans({ location, ...props }): JSX.Element {
    const {
      subscription,
      loading: subscriptionLoading,
      error: subscriptionError,
    } = useSelector((state: ReduxState) => state.Subscription);

    const {
      plans,
      loading: plansLoading,
      error: plansError,
    } = useSelector((state: ReduxState) => state.Plans);

    const dispatch = useDispatch();

    const { coupon } = queryString.parse(location.search);

    React.useEffect(() => {
      dispatch(fetchPlans(coupon as string));
      dispatch(fetchSubscription());
    }, [dispatch]);

    if (subscriptionLoading || plansLoading) {
      return (
        <ClipLoader
          // sizeUnit={"px"}
          size={35}
          // color={"#123abc"}
          loading={true}
        />
      );
    }

    if (!subscription || subscriptionError) {
      return (
        <Alert variant="danger">
          {subscriptionError || "Error getting subscription"}
        </Alert>
      );
    }
    if ((plans.length === 0 || plansError) && subscription?.status !== "sandbox") {
      return (
        <Alert variant="danger">
          {subscriptionError || "Error getting plans"}
        </Alert>
      );
    }
    return (
      <WrappedComponent
        {...props}
        subscription={subscription}
        plans={plans}
      ></WrappedComponent>
    );
  };
}

export const getDefaultCard = (customer: StripeCustomer) => {
  if (!customer) {
    return null;
  }
  const defaultSource = customer.default_source;
  if (defaultSource) {
    const maybeSource = customer.sources.data.filter(
      (x) => x.id === defaultSource
    );
    if (maybeSource.length > 0) {
      return maybeSource[0];
    }
  }
  return null;
};
